import React, { useEffect, useState } from 'react';
import {
  Box, Typography, useTheme, Link as MLink, useMediaQuery, IconButton,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 
import TrendItem from '../components/ui/TrendItem';
import apiService from '../services/api';
import { API_ENDPOINTS } from '../utils/apiUrls';

const TrendsPreview = ({ layout = 'row', itemWidth, isOnsaMarket = false, cardSize = 'md' }) => {
  const [trends, setTrends] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useLocation();

  const isFullPage = pathname === '/trends';
  const isDashboard = pathname === '/' || pathname === '/dashboard';

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const requestedCount = isFullPage ? 10 : 6;
        const res = await apiService.get(
          API_ENDPOINTS.TRENDING_PAIRS.GET_ALL,
          { count: requestedCount }
        );
        const list = Array.isArray(res) ? res : (res?.items ?? []);
        setTrends(list.slice(0, requestedCount));
      } catch (error) {
        console.error('Trends fetch error:', error);
      }
    };
    fetchTrends();
  }, [isFullPage]);

  const fillColumn = !isFullPage && isOnsaMarket && layout === 'column';
  const showGridPreview = !isFullPage && layout === 'row' && !fillColumn;

  const previewGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(1, minmax(0, 1fr))',
      sm: 'repeat(3, minmax(160px, 1fr))',
      md: 'repeat(3, minmax(200px, 1fr))',
    },
    gap: { xs: 2, sm: 2, md: 2.5 },
    width: '100%',
    justifyItems: 'stretch',
    '@media (min-width:950px)': {
      gridTemplateColumns: 'repeat(2, minmax(260px, 1fr))',
    },
  };

  const cardWidth = () => {
    if (fillColumn) return { xs: '100%' };
    if (isFullPage) return { xs: '100%' };
    if (itemWidth) return { xs: '100%', sm: itemWidth };
    if (layout === 'column') return { xs: '100%', sm: 360 };
    if (isOnsaMarket) return { xs: 'calc(50% - 8px)', sm: 190, md: 200 };
    return { xs: '100%', sm: 190, md: 200 };
  };

  return (
    <Box
      sx={{
        p: isFullPage ? { xs: 2, md: 3 } : (layout === 'column' ? 0 : 2),
        pb: isFullPage ? { xs: 9, md: 12 } : undefined,
      }}
    >
      {/* HEADER */}
      {isMobile ? (
        isFullPage ? (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{ borderBottom: 'none', pb: 0 }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ textAlign: 'left' }}>
              <MLink component={RouterLink} to="/trends" underline="hover" color="inherit">
                Trends
              </MLink>
            </Typography>
            <IconButton component={RouterLink} to="/onsa-market" size="small" color="inherit">
              <ArrowBackIcon />
            </IconButton>
          </Box>
        ) : isDashboard ? (
          <Box mb={2} sx={{ borderBottom: '1px solid #ccc', pb: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold"><MLink component={RouterLink} to="/trends" underline="hover" color="inherit">
              Trends
            </MLink></Typography>
          </Box>
        ) : (
          <Box mb={2} sx={{ borderBottom: '1px solid #ccc', pb: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold"><MLink component={RouterLink} to="/trends" underline="hover" color="inherit">
              Trends
            </MLink></Typography>
          </Box>
        )
      ) : (
        // DESKTOP
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          sx={
            isFullPage
              ? {}
              : layout === 'column'
                ? { p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#f5f5f5' }
                : { borderBottom: '1px solid #ccc', pb: 1 }
          }
        >
          <Typography variant={isFullPage ? 'h5' : 'h6'} fontWeight="bold"><MLink component={RouterLink} to="/trends" underline="hover" color="inherit">
            Trends
          </MLink></Typography>

          {isFullPage ? (
            <IconButton component={RouterLink} to="/onsa-market" size="small" color="inherit">
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <MLink component={RouterLink} to="/trends" underline="always" color="inherit">
              See more
            </MLink>
          )}
        </Box>
      )}

      {isFullPage ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          {trends.map((item) => (
            <Box key={item.ID || item.id || item.pair} sx={{ width: '100%' }}>
              <TrendItem data={item} isOnsaMarket size="lg" />
            </Box>
          ))}
        </Box>
      ) : (
        showGridPreview ? (
          <Box sx={previewGridSx}>
            {trends.map((item) => (
              <Box key={item.ID || item.id || item.pair} sx={{ width: '100%', minWidth: 0 }}>
                <TrendItem data={item} isOnsaMarket={isOnsaMarket} size={cardSize} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: fillColumn ? 'column' : { xs: isOnsaMarket ? 'row' : 'column', sm: layout === 'column' ? 'column' : 'row' },
              flexWrap: fillColumn ? 'nowrap' : { xs: isOnsaMarket ? 'wrap' : 'nowrap', sm: layout === 'row' ? 'wrap' : 'nowrap' },
              gap: { xs: 2, sm: isOnsaMarket && layout === 'column' ? 3 : 2, md: isOnsaMarket && layout === 'column' ? 2.5 : 2 },
              alignItems: 'stretch',
            }}
          >
            {trends.map((item) => (
              <Box
                key={item.ID || item.id || item.pair}
                sx={{
                  width: cardWidth(item),
                  flex: fillColumn ? '1 1 auto' : (layout === 'column' ? { sm: 'none' } : '0 0 auto'),
                  minWidth: 0,
                }}
              >
                <TrendItem data={item} isOnsaMarket={isOnsaMarket} size={cardSize} />
              </Box>
            ))}
          </Box>
        )
      )}

      {!isFullPage && isMobile && (
        <Box mt={2} textAlign="center">
          <MLink component={RouterLink} to="/trends" underline="always" color="inherit">
            See more
          </MLink>
        </Box>
      )}
    </Box>
  );
};

export default TrendsPreview;
